import React from "react";
import { BarChart, CartesianGrid, XAxis, YAxis, Legend, Bar, Tooltip, ResponsiveContainer } from "recharts";
import { Select } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { IconV3 } from "@rpi/openapi-core";
import { Body, Heading } from "../../../components/Typography";
import { ChartTooltip } from "../../../components/graphs/ChartTooltip";
import { ChartLegend } from "../../../components/graphs/ChartLegend";
import { useStyles } from "./styles";
import { useYtdData } from "./useYtdData";
import { formatCurrencyV2 } from "../../../utils/misc.util";
import { GraphQL } from "@rpi/openapi-api";
import {
  TransformedPaymentDataPoint,
  emptyPaymentMonths,
  fillMissingPaymentMonths,
  formatYearMonth,
} from "./transformer";
import { useAuthUser } from "../../../hooks/useAuthUser";

type TransformedPaymentsChartQuery = {
  paymentsChart: {
    availableYears: number[];
    nextChargeDate: string | null;
    dataPoints: TransformedPaymentDataPoint[];
  };
};

const DEFAULT_CURRENCY = GraphQL.SupportedCurrency.Usd;

export const YtdPaymentsChart: React.FC = () => {
  const { isSandbox } = useAuthUser();
  const { classes } = useStyles();
  const timezone = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const [selectedYear, setSelectedYear] = React.useState<number>(new Date().getFullYear());

  const { data, isLoading } = GraphQL.usePaymentsChartQuery(
    {
      isSandbox,
      timezone,
      year: selectedYear,
    },
    {
      enabled: !isSandbox,
      select: (data: GraphQL.PaymentsChartQuery): TransformedPaymentsChartQuery => ({
        paymentsChart: {
          availableYears: data.paymentsChart?.availableYears ?? [],
          nextChargeDate: data.paymentsChart?.nextChargeDate ?? null,
          dataPoints: fillMissingPaymentMonths(data.paymentsChart?.dataPoints ?? [], selectedYear, DEFAULT_CURRENCY),
        },
      }),
    }
  );

  const { selectProps } = useYtdData({
    isLoading,
    availableYears: data?.paymentsChart?.availableYears,
    selectedYear,
    setSelectedYear,
  });

  const chartData = isSandbox
    ? emptyPaymentMonths(selectedYear, DEFAULT_CURRENCY)
    : data?.paymentsChart?.dataPoints ?? [];

  // Read the date out before memoizing: an optional-chain expression in the dependency array
  // is opaque to the compiler, so it cannot prove the memo is safe to keep.
  const nextChargeDate = data?.paymentsChart?.nextChargeDate;
  const nextChargeDateFormatted = React.useMemo(() => {
    if (!nextChargeDate) return null;
    const date = new Date(nextChargeDate);
    return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
  }, [nextChargeDate]);

  return (
    <div className={classes.chartContainer}>
      <div className={classes.chartHeader}>
        <Heading size="lg">Payments</Heading>
        <Select className={classes.chartSelect} {...selectProps} />
      </div>
      <Body className={classes.chartSubheader}>
        {nextChargeDateFormatted ? `Next charge: ${nextChargeDateFormatted}` : "Next charge: N/A"}
      </Body>
      <ResponsiveContainer>
        <BarChart className={classes.chart} data={chartData}>
          <CartesianGrid />
          <XAxis
            dataKey="month"
            axisLine={false}
            tickFormatter={(value: string) => formatYearMonth(value, { month: "short" })}
          />
          <YAxis
            dataKey="netPaymentAmount"
            width="auto"
            domain={[0, "auto"]}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => {
              const currency = chartData[0]?.currency ?? DEFAULT_CURRENCY;
              return removeDecimalsFromCurrencyValue(currency, Number(value));
            }}
          />
          {!isSandbox && <Legend content={ChartLegend} />}
          {!isSandbox && (
            <Tooltip
              content={(props) => {
                const currency = props?.payload?.[0]?.payload.currency ?? DEFAULT_CURRENCY;
                const monthDate = props?.payload?.[0]?.payload.month as string | undefined;
                const label = monthDate ? formatYearMonth(monthDate, { month: "short", year: "numeric" }) : "";
                return (
                  <ChartTooltip
                    {...props}
                    label={label}
                    formatValue={(value) => formatCurrencyV2(currency, Number(value))}
                  />
                );
              }}
              cursor={{
                fill: tokens.color.semantic.bg.neutralInverseHover,
                fillOpacity: 0.06,
              }}
            />
          )}
          <Bar
            dataKey="netPaymentAmount"
            name="Net Payment Amount"
            fill={tokens.color.primitive.light.dataVisualization.teal}
          />
        </BarChart>
      </ResponsiveContainer>
      {isSandbox && <SandboxOverlay />}
    </div>
  );
};

const SandboxOverlay: React.FC = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.sandboxOverlay} data-testid="payments-sandbox-overlay">
      <IconV3 icon="bar_chart_off" size={24} color={tokens.color.semantic.icon.subtle} />
      <Body size="lg" weight="bold">
        No payments in Sandbox
      </Body>
      <Body>Switch to Production to view payment data</Body>
    </div>
  );
};

function removeDecimalsFromCurrencyValue(
  currency: GraphQL.SupportedCurrency = GraphQL.SupportedCurrency.Usd,
  value = 0
) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}
